const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || "G-5N2VB0XYRP";
const ANALYTICS_COOKIE_KEYS = [
  "measure_content_performance",
  "audience_insight",
  "improve_services",
];
const PRODUCTION_HOSTS = new Set(["mockee.io", "www.mockee.io"]);

let analyticsConfigured = false;
let trafficSegment = "visitor";

const isAnalyticsRuntime = () => {
  if (typeof window === "undefined") return false;
  return (
    PRODUCTION_HOSTS.has(window.location.hostname) ||
    process.env.REACT_APP_ENABLE_ANALYTICS === "true"
  );
};

const readCookiePreferences = () => {
  if (typeof document === "undefined") return {};
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith("cookie_preferences="));
  if (!cookie) return {};

  try {
    return JSON.parse(decodeURIComponent(cookie.split("=").slice(1).join("=")));
  } catch {
    return {};
  }
};

export const hasAnalyticsConsent = () => {
  const preferences = readCookiePreferences();
  return ANALYTICS_COOKIE_KEYS.every((key) => preferences[key] === true);
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
};

const loadAnalytics = () => {
  if (!isAnalyticsRuntime() || analyticsConfigured) return;

  ensureGtag();
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  if (!document.querySelector(`script[data-ga-id="${GA_MEASUREMENT_ID}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.dataset.gaId = GA_MEASUREMENT_ID;
    document.head.appendChild(script);
  }

  analyticsConfigured = true;
};

const removeAnalyticsCookies = () => {
  if (typeof document === "undefined") return;
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name.startsWith("_ga")) return;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.mockee.io; SameSite=Lax`;
  });
};

export const initializeAnalytics = () => {
  if (isAnalyticsRuntime() && hasAnalyticsConsent()) loadAnalytics();
};

export const updateAnalyticsConsent = (granted) => {
  if (!isAnalyticsRuntime()) return;

  if (granted) {
    loadAnalytics();
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    window.gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  if (window.gtag) {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
  removeAnalyticsCookies();
};

export const setAnalyticsUserRole = (role) => {
  trafficSegment =
    role === "A"
      ? "staff"
      : role === "S"
        ? "seller_test"
        : role === "F"
          ? "affiliate_test"
          : role === "P"
            ? "buyer"
            : "visitor";

  if (!isAnalyticsRuntime() || !hasAnalyticsConsent()) return;
  loadAnalytics();
  window.gtag("set", "user_properties", {
    account_segment: trafficSegment,
  });
};

const canTrack = () => isAnalyticsRuntime() && hasAnalyticsConsent();

export const pageview = (url) => {
  if (!canTrack()) return;
  loadAnalytics();
  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: url,
    page_title: document.title,
    traffic_segment: trafficSegment,
  });
};

export const trackEvent = (eventName, parameters = {}) => {
  if (!canTrack()) return;
  loadAnalytics();
  window.gtag("event", eventName, {
    ...parameters,
    traffic_segment: trafficSegment,
  });
};

export const getTemplateAnalyticsParams = (templateId, template) => ({
  template_id: String(templateId || template?.id || "unknown"),
  template_access:
    template?.is_free === true
      ? "free"
      : template?.is_free === false
        ? "paid"
        : "unknown",
});

export const getSafeExportErrorCategory = (error) => {
  const message = String(error?.message || "").toLowerCase();
  if (/network|fetch|http/.test(message)) return "network";
  if (/photopea|asset|render|image/.test(message)) return "rendering";
  if (/psd|arraybuffer|boolean|descriptor/.test(message))
    return "template_processing";
  if (/permission|authori[sz]|login|token/.test(message))
    return "authorization";
  return "unknown";
};
